import React, { useState, useEffect, useCallback } from 'react';
import { Container, Form, Button, Row, Col, Table, Card, Alert } from 'react-bootstrap';
import { Link } from 'react-router-dom';
import { toast } from 'react-toastify';
import { FaChartLine, FaCalendarAlt, FaArrowUp, FaArrowDown, FaHome } from 'react-icons/fa';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend,
} from 'chart.js';
import { Line } from 'react-chartjs-2';
import { format, subMonths, startOfMonth, endOfMonth, eachMonthOfInterval, parseISO } from 'date-fns';
import { ptBR } from 'date-fns/locale';
import transactionService from '../services/transactionService';
import { formatCurrency } from '../utils/formatters';

// Registrar componentes do Chart.js
ChartJS.register(CategoryScale, LinearScale, PointElement, LineElement, Title, Tooltip, Legend);

const TemporalAnalysis = () => {
  const [loading, setLoading] = useState(false);
  const [analysisData, setAnalysisData] = useState({
    periods: [],
    currentPeriodTotal: 0,
    previousPeriodTotal: 0,
    variation: 0,
    trend: 'stable' // 'up', 'down', 'stable'
  });
  const [filters, setFilters] = useState({
    months: 6, // Número de meses para análise
    analysisType: 'expenses' // 'expenses', 'incomes', 'both'
  });

  const processTemporalData = useCallback(async () => {
    if (!filters.months) return;
    
    try {
      setLoading(true);
      
      // Calcular período de análise
      const endDate = endOfMonth(new Date());
      const startDate = startOfMonth(subMonths(endDate, filters.months - 1));
      
      // Buscar todas as transações do período
      const transactions = await transactionService.search({
        startDate: format(startDate, 'yyyy-MM-dd'),
        endDate: format(endDate, 'yyyy-MM-dd')
      });
      
      // Gerar lista de meses do período
      const monthsInterval = eachMonthOfInterval({ start: startDate, end: endDate });
      
      // Processar dados por mês
      const periodsData = monthsInterval.map(monthDate => {
        const monthKey = format(monthDate, 'yyyy-MM');
        const monthTransactions = transactions.filter(t => 
          format(parseISO(t.date), 'yyyy-MM') === monthKey
        );
        
        let expenses = 0;
        let incomes = 0;
        
        monthTransactions.forEach(transaction => {
          const amount = Math.abs(transaction.amount);
          if (transaction.nature === 'EXPENSE') {
            expenses += amount;
          } else {
            incomes += amount;
          }
        });
        
        return {
          month: monthKey,
          label: format(monthDate, 'MMM/yyyy', { locale: ptBR }),
          fullLabel: format(monthDate, 'MMMM yyyy', { locale: ptBR }),
          expenses,
          incomes,
          net: incomes - expenses,
          transactionCount: monthTransactions.length
        };
      });
      
      // Calcular métricas de comparação
      const currentMonth = periodsData[periodsData.length - 1];
      const previousMonth = periodsData[periodsData.length - 2];
      
      let currentPeriodTotal = 0;
      let previousPeriodTotal = 0;
      
      if (filters.analysisType === 'expenses') {
        currentPeriodTotal = currentMonth?.expenses || 0;
        previousPeriodTotal = previousMonth?.expenses || 0;
      } else if (filters.analysisType === 'incomes') {
        currentPeriodTotal = currentMonth?.incomes || 0;
        previousPeriodTotal = previousMonth?.incomes || 0;
      } else {
        currentPeriodTotal = currentMonth?.net || 0;
        previousPeriodTotal = previousMonth?.net || 0;
      }
      
      const variation = previousPeriodTotal !== 0 
        ? ((currentPeriodTotal - previousPeriodTotal) / previousPeriodTotal) * 100 
        : 0;
      
      const trend = variation > 5 ? 'up' : variation < -5 ? 'down' : 'stable';
      
      setAnalysisData({
        periods: periodsData,
        currentPeriodTotal,
        previousPeriodTotal,
        variation,
        trend
      });
      
    } catch (error) {
      console.error('Erro ao carregar dados temporais:', error);
      toast.error('Erro ao carregar análise temporal. Tente novamente.');
    } finally {
      setLoading(false);
    }
  }, [filters.months, filters.analysisType]);

  useEffect(() => {
    processTemporalData();
  }, [processTemporalData]);

  const handleFilterChange = (e) => {
    const { name, value } = e.target;
    setFilters(prev => ({ ...prev, [name]: value }));
  };

  const getChartData = () => {
    const labels = analysisData.periods.map(p => p.label);
    
    const datasets = [];
    
    if (filters.analysisType === 'expenses' || filters.analysisType === 'both') {
      datasets.push({
        label: 'Despesas',
        data: analysisData.periods.map(p => p.expenses),
        borderColor: 'rgb(220, 53, 69)',
        backgroundColor: 'rgba(220, 53, 69, 0.1)',
        tension: 0.4,
        fill: filters.analysisType !== 'both'
      });
    }
    
    if (filters.analysisType === 'incomes' || filters.analysisType === 'both') {
      datasets.push({
        label: 'Receitas',
        data: analysisData.periods.map(p => p.incomes),
        borderColor: 'rgb(25, 135, 84)',
        backgroundColor: 'rgba(25, 135, 84, 0.1)',
        tension: 0.4,
        fill: filters.analysisType !== 'both'
      });
    }
    
    if (filters.analysisType === 'both') {
      datasets.push({
        label: 'Saldo Líquido',
        data: analysisData.periods.map(p => p.net),
        borderColor: 'rgb(13, 110, 253)',
        backgroundColor: 'rgba(13, 110, 253, 0.1)',
        tension: 0.4,
        borderDash: [5, 5]
      });
    }
    
    return { labels, datasets };
  };

  const chartOptions = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: {
        position: 'top',
      },
      title: {
        display: true,
        text: `Evolução ${filters.analysisType === 'expenses' ? 'das Despesas' : 
               filters.analysisType === 'incomes' ? 'das Receitas' : 'Financeira'} - Últimos ${filters.months} meses`,
        font: {
          size: 16
        }
      },
      tooltip: {
        callbacks: {
          label: function(context) {
            const label = context.dataset.label || '';
            const value = formatCurrency(context.raw);
            return `${label}: ${value}`;
          }
        }
      }
    },
    scales: {
      y: {
        beginAtZero: true,
        ticks: {
          callback: function(value) {
            return formatCurrency(value);
          }
        }
      }
    }
  };

  const getTrendIcon = () => {
    if (analysisData.trend === 'up') return <FaArrowUp className="text-success" />;
    if (analysisData.trend === 'down') return <FaArrowDown className="text-danger" />;
    return <FaCalendarAlt className="text-muted" />;
  };

  const getTrendMessage = () => {
    const typeLabel = filters.analysisType === 'expenses' ? 'gastos' : 
                     filters.analysisType === 'incomes' ? 'receitas' : 'saldo líquido';
    
    if (analysisData.trend === 'up') {
      return `Seus ${typeLabel} aumentaram ${Math.abs(analysisData.variation).toFixed(1)}% em relação ao mês anterior`;
    }
    if (analysisData.trend === 'down') {
      return `Seus ${typeLabel} diminuíram ${Math.abs(analysisData.variation).toFixed(1)}% em relação ao mês anterior`;
    }
    return `Seus ${typeLabel} se mantiveram estáveis em relação ao mês anterior`;
  };

  const getBestWorstMonths = () => {
    if (analysisData.periods.length === 0) return { best: null, worst: null };
    
    const values = analysisData.periods.map(p => {
      if (filters.analysisType === 'expenses') return p.expenses;
      if (filters.analysisType === 'incomes') return p.incomes;
      return p.net;
    });
    
    const maxValue = Math.max(...values);
    const minValue = Math.min(...values);
    
    const bestMonth = analysisData.periods.find(p => {
      const value = filters.analysisType === 'expenses' ? p.expenses : 
                   filters.analysisType === 'incomes' ? p.incomes : p.net;
      return value === (filters.analysisType === 'expenses' ? minValue : maxValue);
    });
    
    const worstMonth = analysisData.periods.find(p => {
      const value = filters.analysisType === 'expenses' ? p.expenses : 
                   filters.analysisType === 'incomes' ? p.incomes : p.net;
      return value === (filters.analysisType === 'expenses' ? maxValue : minValue);
    });
    
    return { best: bestMonth, worst: worstMonth };
  };

  const { best } = getBestWorstMonths();

  return (
    <Container>
      <div className="sheet-container">
        <div className="sheet-header">
          <div>
            <h5 className="sheet-title">
              <FaChartLine className="me-2" />
              Análise Temporal
            </h5>
            <Alert variant="primary" className="mt-2 mb-0">
              <strong>Como meus gastos evoluem ao longo do tempo?</strong> 
              <span className="text-muted"> - Identifique tendências e padrões nos seus gastos mensais</span>
            </Alert>
          </div>
          <div className="sheet-toolbar">
            <Button 
              as={Link}
              to="/reports"
              variant="outline-primary"
            >
              <FaHome className="me-1" /> Central de Relatórios
            </Button>
          </div>
        </div>
        
        <div className="sheet-content">
          {/* Filtros */}
          <Row className="mb-4">
            <Col md={4}>
              <Form.Group>
                <Form.Label>Período de Análise</Form.Label>
                <Form.Select
                  name="months"
                  value={filters.months}
                  onChange={handleFilterChange}
                  className="excel-form-control"
                >
                  <option value={3}>Últimos 3 meses</option>
                  <option value={6}>Últimos 6 meses</option>
                  <option value={12}>Último ano</option>
                  <option value={24}>Últimos 2 anos</option>
                </Form.Select>
              </Form.Group>
            </Col>
            <Col md={4}>
              <Form.Group>
                <Form.Label>Tipo de Análise</Form.Label>
                <Form.Select
                  name="analysisType"
                  value={filters.analysisType}
                  onChange={handleFilterChange}
                  className="excel-form-control"
                >
                  <option value="expenses">Apenas Despesas</option>
                  <option value="incomes">Apenas Receitas</option>
                  <option value="both">Ambos + Saldo Líquido</option>
                </Form.Select>
              </Form.Group>
            </Col>
          </Row>

          {loading ? (
            <div className="text-center my-4">
              <span className="spinner-border text-primary" role="status"></span>
              <p className="mt-2">Carregando análise temporal...</p>
            </div>
          ) : analysisData.periods.length > 0 ? (
            <>
              {/* Métricas Principais */}
              <Row className="mb-4">
                <Col md={4}>
                  <Card className="text-center">
                    <Card.Body>
                      <h6 className="text-muted">Mês Atual</h6>
                      <h4 className={filters.analysisType === 'expenses' ? 'text-danger' : 'text-success'}>
                        {formatCurrency(analysisData.currentPeriodTotal)}
                      </h4>
                      <small className="text-muted">
                        {analysisData.periods[analysisData.periods.length - 1]?.fullLabel}
                      </small>
                    </Card.Body>
                  </Card>
                </Col>
                <Col md={4}>
                  <Card className="text-center">
                    <Card.Body>
                      <h6 className="text-muted">Variação</h6>
                      <h4 className={analysisData.variation >= 0 ? 'text-success' : 'text-danger'}>
                        {getTrendIcon()} {Math.abs(analysisData.variation).toFixed(1)}%
                      </h4>
                      <small className="text-muted">vs mês anterior</small>
                    </Card.Body>
                  </Card>
                </Col>
                <Col md={4}>
                  <Card className="text-center">
                    <Card.Body>
                      <h6 className="text-muted">
                        {filters.analysisType === 'expenses' ? 'Menor Gasto' : 'Maior Receita'}
                      </h6>
                      <h4 className="text-primary">
                        {best && formatCurrency(
                          filters.analysisType === 'expenses' ? best.expenses : 
                          filters.analysisType === 'incomes' ? best.incomes : best.net
                        )}
                      </h4>
                      <small className="text-muted">{best?.fullLabel}</small>
                    </Card.Body>
                  </Card>
                </Col>
              </Row>

              {/* Insight da Tendência */}
              <Alert variant={analysisData.trend === 'up' ? 'success' : analysisData.trend === 'down' ? 'warning' : 'info'} className="mb-4">
                <strong>Tendência:</strong> {getTrendMessage()}
              </Alert>

              {/* Gráfico Principal */}
              <Card className="mb-4">
                <Card.Header>
                  <h6 className="mb-0">
                    <FaChartLine className="me-2 text-primary" />
                    Evolução Temporal
                  </h6>
                </Card.Header>
                <Card.Body>
                  <div style={{ height: '400px' }}>
                    <Line data={getChartData()} options={chartOptions} />
                  </div>
                </Card.Body>
              </Card>

              {/* Tabela Detalhada */}
              <Card>
                <Card.Header>
                  <h6 className="mb-0">Resumo Mensal Detalhado</h6>
                </Card.Header>
                <Card.Body>
                  <Table responsive hover className="excel-table">
                    <thead>
                      <tr>
                        <th>Mês</th>
                        <th className="text-end">Receitas</th>
                        <th className="text-end">Despesas</th>
                        <th className="text-end">Saldo Líquido</th>
                        <th className="text-center">Transações</th>
                      </tr>
                    </thead>
                    <tbody>
                      {analysisData.periods.slice().reverse().map((period) => (
                        <tr key={period.month}>
                          <td>
                            <strong>{period.fullLabel}</strong>
                          </td>
                          <td className="text-end text-success">
                            {formatCurrency(period.incomes)}
                          </td>
                          <td className="text-end text-danger">
                            {formatCurrency(period.expenses)}
                          </td>
                          <td className={`text-end fw-bold ${period.net >= 0 ? 'text-success' : 'text-danger'}`}>
                            {formatCurrency(period.net)}
                          </td>
                          <td className="text-center">
                            {period.transactionCount}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </Table>
                </Card.Body>
              </Card>
            </>
          ) : (
            <div className="text-center my-4">
              <p>Nenhum dado encontrado para o período selecionado</p>
            </div>
          )}
        </div>
      </div>
    </Container>
  );
};

export default TemporalAnalysis; 